/**
 * Sprint V100+ · KlickTipp-DOI Bestätigungsseite (Landing nach DOI-Klick).
 * Route: /webinar/doi-bestaetigt?subscriberId=…&listId=…&subscriberKey=…
 *
 * V100+ Iteration 3 (18.09.2026) — Auto-Login-Handoff aktiviert:
 *   Wenn subscriberKey vorhanden → POST /api/auth/doi/handoff → Session-Cookie
 *   gesetzt → magic_link_recovery_pending wird gecleart (KEINE zweite Mail).
 *   Danach Redirect zu /webinar/willkommen (mit Auto-Login-Kontext).
 *   Falls Handoff fehlschlägt (z.B. in-App-Browser, Cookies-Block) → weiter
 *   zu /webinar/willkommen (User bekommt trotzdem die Info + optional
 *   Recovery-Magic-Link nach 5 Min).
 *
 * Datensparsam: subscriberKey ist ein KT-verschlüsselter Contact-Key
 * (keine Klartext-E-Mail).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function WebinarDoiConfirmed() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const subscriberKey = params.get("subscriberKey") || "";
  const subscriberId = params.get("subscriberId") || "";
  const listId = params.get("listId") || "";
  const [phase, setPhase] = useState("handoff"); // handoff | done | skip

  useEffect(() => {
    const k = (subscriberKey || subscriberId || "").trim();
    if (!k) {
      // Kein Key -> überspringe Handoff, direkt weiter
      setPhase("skip");
      setTimeout(() => {
        navigate(`/webinar/willkommen`, { replace: true });
      }, 1500);
      return;
    }

    const attemptHandoff = async () => {
      try {
        await api.post("/auth/doi/handoff", { k });
        // Auth-Context refreshen — magic_link_recovery_pending ist jetzt cleared
        try { await refresh(); } catch { /* silent */ }
      } catch {
        // Best-effort. Bei Fehler fällt der User trotzdem auf die
        // Willkommen-Seite; nach 5 Min feuert der Recovery-Scheduler die Mail
        // als Rettungsanker (Sprint V39-Architektur).
      } finally {
        setPhase("done");
        const q = new URLSearchParams();
        if (subscriberKey) q.set("subscriberKey", subscriberKey);
        if (listId) q.set("listId", listId);
        setTimeout(() => {
          navigate(`/webinar/willkommen${q.toString() ? `?${q}` : ""}`, {
            replace: true,
          });
        }, 900);
      }
    };
    attemptHandoff();
  }, [navigate, refresh, subscriberKey, subscriberId, listId]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center px-4 py-12"
      data-testid="webinar-doi-confirmed"
    >
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-24 h-24 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mb-6 animate-in fade-in zoom-in duration-500">
          {phase === "handoff" ? (
            <Loader2 className="w-12 h-12 text-emerald-600 dark:text-emerald-300 animate-spin" />
          ) : (
            <CheckCircle2 className="w-14 h-14 text-emerald-600 dark:text-emerald-300" />
          )}
        </div>
        <p className="text-xs uppercase tracking-widest text-[#F3651D] font-bold mb-2">
          Bestätigt
        </p>
        <h1
          className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white"
          data-testid="webinar-doi-confirmed-headline"
        >
          E-Mail bestätigt.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
          Danke — dein Platz ist offiziell reserviert.
        </p>
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {phase === "handoff"
            ? "Wir melden dich gleich automatisch an\u2026"
            : "Gleich geht's weiter\u2026"}
        </p>
      </div>
    </div>
  );
}
